import {
  drugQuantity,
  drugTypeOf,
  formatImagingNote,
  isAsNeeded,
  parseImagingNote,
  supplyTypeOf,
  type DrugOrderType,
  type LabTest,
} from "./ordersModelV4";
import type {
  CatalogItem,
  EditableKind,
  Icd9Option,
  SaveSetParams,
  SetEntry,
  SheetKind,
  SheetLine,
} from "../common/sequence/OrderSheetV4";

// ---------- ค่าเริ่มต้นของบรรทัด ----------

export const NOTE_ITEM_NAME = "Note Order (คำสั่งแพทย์ถึงพยาบาล)";

export const DEFAULT_FIELDS: Record<SheetKind, Record<string, string>> = {
  imaging: { side: "-", view: "", urgency: "ROUTINE", preg: "ไม่ตั้งครรภ์", ind: "" },
  treatment: { performer: "NURSE", icd: "", icd9: "", detail: "", equipment: "" },
  drug: {
    priority: "ROUTINE",
    home: "Y",
    dose: "",
    unit: "",
    route: "",
    site: "",
    frequency: "",
    prn: "",
    duration: "",
    qty: "",
    description: "",
  },
  supply: { home: "Y", use: "REFILL_OPD", costCenter: "", note: "" },
  note: { detail: "", reason: "" },
  df: { employee: "" },
  misc: { note: "" },
};

const PERFORMER_CODES: Record<number, string> = { 1: "DOCTOR", 2: "NURSE", 3: "OTHER" };

const SUPPLY_TYPE_CODES: Record<number, string> = {
  1: "HOME_OPD",
  3: "REFILL_OPD",
  7: "COST_CENTER",
};

export const toCatalog = (
  kind: SheetKind,
  items: Record<string, any>[] | undefined,
  priceOf: (item: Record<string, any>) => unknown,
): CatalogItem[] =>
  (items ?? []).map((item) => {
    const price = priceOf(item);

    return {
      key: `${kind}_${item.id}`,
      kind,
      id: item.id,
      name: item.name,
      price: price == null || price === "" ? null : Number(price),
      raw: item,
    };
  });

export const withDrugQuantity = (line: SheetLine, frequencies: Map<string, number>): SheetLine => ({
  ...line,
  quantity: drugQuantity(line, frequencies),
  raw: { ...line.raw, asNeeded: isAsNeeded(line.fields, frequencies) },
});

// ---------- ส่งคำสั่งเข้า API ----------

export const toDrugItem = (line: SheetLine) => ({
  product: line.product,
  dose: Number(line.fields.dose) || 0,
  unit: Number(line.fields.unit) || null,
  route: Number(line.fields.route) || null,
  frequency: Number(line.fields.frequency) || null,
  prn: line.fields.prn?.trim() ?? "",
  duration: line.raw.asNeeded ? 0 : Number(line.fields.duration) || 0,
  quantity: line.quantity,
  description: line.fields.description,
  note_adverse_reaction: line.fields.adrNote ?? "",
  note_drug_interaction: line.fields.interactionNote ?? "",
  note_drug_lab_interaction: line.fields.labInteractionNote ?? "",
  priority: "ROUTINE",
  site: Number(line.fields.site) || "",
  method: null,
});

export const drugTypeOfOrder = (specificType = ""): DrugOrderType => {
  if (specificType.includes("stat")) {
    return "STAT";
  }

  return !specificType || specificType.includes("home") ? "HOME_OPD" : "ONE_DOSE";
};

export const toCoreProcedure = (line: SheetLine, emrId?: number) => {
  if (!line.fields.icd9) {
    return [];
  }

  const icd9: Icd9Option & { procedureId?: number } = JSON.parse(line.fields.icd9);

  return [
    {
      ...(icd9.procedureId ? { id: icd9.procedureId } : {}),
      emr: emrId ?? null,
      icd9cm: icd9.id,
      icd9cm_code: icd9.code,
      icd9cm_term: icd9.term,
      icd9cm_med_term: null,
      medical_description: icd9.medterm || icd9.term,
      type: "PRIMARY",
    },
  ];
};

// ---------- คำสั่งเดิมของ encounter -> บรรทัด ----------

export const toOrderLines = (
  kind: EditableKind,
  res: Record<string, any>,
  prices: number[],
  specificType?: string,
): SheetLine[] => {
  const base = (item: Record<string, any>, index: number) => ({
    key: `order_${res.id}_${item.id ?? index}`,
    itemKey: `${kind}_${item.product ?? 0}`,
    kind,
    product: item.product ?? null,
    name: item.product_name ?? NOTE_ITEM_NAME,
    quantity: Number(item.quantity || 1),
    masterPrice: Number(item.unit_price || prices[index] || 0),
    priceUnit: Number(item.unit_price || prices[index] || 0),
    raw: { ...item, orderExtra: res.extra },
    orderId: res.id as number,
    orderItemId: item.id,
  });

  if (kind === "drug") {
    const drugType = drugTypeOfOrder(specificType);

    return ((res.items ?? []) as Record<string, any>[]).map((item, index) => {
      const value = (key: string) => (item[key] == null ? "" : String(item[key]));
      const price = Number(item.price_unit || 0);

      return {
        ...base(item, index),
        name: item.drug_name_from_format || item.title || "",
        masterPrice: price,
        priceUnit: price,
        fields: {
          ...DEFAULT_FIELDS.drug,
          priority: drugType === "STAT" ? "STAT" : "ROUTINE",
          home: drugType === "HOME_OPD" ? "Y" : "N",
          dose: value("dose"),
          unit: value("unit"),
          route: value("route"),
          site: value("site"),
          frequency: value("frequency"),
          prn: value("prn"),
          duration: value("duration"),
          qty: item.prn || !Number(item.duration) ? String(Number(item.quantity) || "") : "",
          description: value("description"),
          adrNote: value("note_adverse_reaction"),
          interactionNote: value("note_drug_interaction"),
          labInteractionNote: value("note_drug_lab_interaction"),
        },
        raw: { ...item, order: res },
      };
    });
  }

  if (kind === "supply") {
    const isHome = res.type === "HOME_OPD";

    return ((res.items ?? []) as Record<string, any>[]).map((item, index) => {
      const price = Number(item.price_unit || 0);

      return {
        ...base(item, index),
        name: item.name ?? "",
        quantity: Number(item.quantity_request || 1),
        masterPrice: price,
        priceUnit: price,
        fields: {
          ...DEFAULT_FIELDS.supply,
          home: isHome ? "Y" : "N",
          use: isHome ? DEFAULT_FIELDS.supply.use : res.type,
        },
        raw: { ...item, order: res },
      };
    });
  }

  if (kind === "df") {
    return [
      {
        ...base({ ...res, product: res.doctor_fee_rule }, 0),
        name: res.doctor_fee_rule_display ?? "",
        masterPrice: Number(res.price_unit || 0),
        priceUnit: Number(res.compensation_price || 0),
        fields: { ...DEFAULT_FIELDS.df, employee: String(res.received_employee ?? "") },
        raw: res,
      },
    ];
  }

  if (kind === "imaging") {
    return ((res.order_items ?? []) as Record<string, any>[]).map((item, index) => ({
      ...base(item, index),
      fields: { ...DEFAULT_FIELDS.imaging, urgency: item.urgency, ...parseImagingNote(item.note) },
    }));
  }

  if (kind === "treatment") {
    const procedure = res.core_procedure?.[0];
    const icd9 = procedure
      ? {
          id: procedure.icd9cm,
          code: procedure.icd9cm_code,
          term: procedure.icd9cm_term,
          medterm: procedure.medical_description ?? "",
          label: `${procedure.icd9cm_code} ${procedure.icd9cm_term}`,
          procedureId: procedure.id,
        }
      : null;

    return ((res.order_items ?? []) as Record<string, any>[]).map((item, index) => ({
      ...base(item, index),
      fields: {
        performer: res.to_be_performer ?? "",
        icd: icd9?.label ?? "",
        icd9: icd9 ? JSON.stringify(icd9) : "",
        detail: res.order_detail ?? "",
        equipment: res.equipment_detail ?? "",
      },
    }));
  }

  return [
    {
      ...base({}, 0),
      fields: {
        detail: res.detail ?? "",
        reason: res.note ?? "",
        ...(res.note ? { onBehalf: "Y" } : {}),
      },
    },
  ];
};

// ---------- Order Set ----------

const codeOf = (codes: Record<number, string>, value: string) =>
  Number(Object.keys(codes).find((code) => codes[Number(code)] === value)) || null;

export const toSetTemplate = (lines: SheetLine[], params: SaveSetParams, division?: number) => {
  const ofKind = (kind: SheetKind) => lines.filter((line) => line.kind === kind);

  return {
    name: params.name,
    ...(params.scope === "mine" ? { type: "P", doctor: params.doctorId } : { type: "D", division }),
    lab_template_list: params.labItems.map((item) => ({
      test: item.product,
      specimen_id: item.specimen,
      specimen_time: item.specimen_time,
      urgency: item.urgency === "STAT" ? 2 : 1,
      note: item.note ?? "",
    })),
    imaging_template_list: ofKind("imaging").map((line) => ({
      product: line.product,
      quantity: line.quantity,
      eligibility_type: 1,
      urgency: line.fields.urgency === "STAT" ? 2 : 1,
      note: formatImagingNote(line.fields),
    })),
    treatment_template_list: ofKind("treatment").map((line) => ({
      product: line.product,
      quantity: line.quantity,
      eligibility_type: 1,
      icd9cm: (JSON.parse(line.fields.icd9) as Icd9Option).id,
      to_be_performer: codeOf(PERFORMER_CODES, line.fields.performer),
      order_detail: line.fields.detail ?? "",
      equipment_detail: line.fields.equipment ?? "",
      remark: "",
    })),
    supply_template_list: ofKind("supply").map((line) => ({
      supply: line.product,
      quantity: line.quantity,
      type: codeOf(SUPPLY_TYPE_CODES, supplyTypeOf(line)),
      eligibility_type: 1,
      remark: "",
    })),
    miscellaneous_template_list: ofKind("misc").map((line) => ({
      product: line.product,
      quantity: line.quantity,
      eligibility_type: 1,
    })),
    drug_template_list: ofKind("drug").map((line) => {
      const item = toDrugItem(line);

      return {
        product: item.product,
        dose: item.dose,
        unit: item.unit,
        route: item.route,
        ...(item.site ? { site: item.site } : {}),
        frequency: item.frequency,
        prn: item.prn,
        duration: item.duration,
        quantity: item.quantity,
        type: drugTypeOf(line.fields),
      };
    }),
    doctor_fee_template_list: ofKind("df").map((line) => ({
      doctor_fee_rule: line.product,
      quantity: line.quantity,
    })),
    note_template_list: ofKind("note").map((line) => ({ detail: line.fields.detail, note: "" })),
  };
};

export const toSetEntries = (
  res: Record<string, any>,
  labTests: Map<string, LabTest>,
): SetEntry[] => {
  const entries: SetEntry[] = [];

  const entry = (
    kind: SheetKind,
    item: Record<string, any>,
    product: number,
    fields: Record<string, string>,
  ): SetEntry => ({
    key: `${kind}_${item.id}`,
    kind,
    name: item.name,
    price: Number(item.price_total || 0),
    quantity: Number(item.quantity || 1),
    item: {
      key: `${kind}_${product}`,
      kind,
      id: product,
      name: item.name,
      price: Number(item.price_normal || 0),
      raw: item,
    },
    fields,
  });

  for (const lab of (res.lab_templates ?? []) as Record<string, any>[]) {
    entries.push({
      key: `lab_${lab.id}`,
      kind: "lab",
      name: lab.name,
      price: Number(lab.price_total || 0),
      quantity: 1,
      lab: labTests.get(lab.code),
    });
  }

  for (const item of (res.imaging_templates ?? []) as Record<string, any>[]) {
    entries.push(
      entry("imaging", item, item.product, {
        ...DEFAULT_FIELDS.imaging,
        urgency: String(item.urgency) === "2" ? "STAT" : "ROUTINE",
        ...(item.note ? parseImagingNote(item.note) : {}),
      }),
    );
  }

  for (const item of (res.treatment_templates ?? []) as Record<string, any>[]) {
    const icd9: Icd9Option | null = item.icd9cm
      ? {
          id: item.icd9cm,
          code: item.icd9cm_code,
          term: item.icd9cm_term,
          medterm: "",
          label: `${item.icd9cm_code} ${item.icd9cm_term}`,
        }
      : null;

    entries.push(
      entry("treatment", item, item.product, {
        ...DEFAULT_FIELDS.treatment,
        performer: PERFORMER_CODES[item.to_be_performer] ?? DEFAULT_FIELDS.treatment.performer,
        icd: icd9?.label ?? "",
        icd9: icd9 ? JSON.stringify(icd9) : "",
        detail: item.order_detail ?? "",
        equipment: item.equipment_detail ?? "",
      }),
    );
  }

  for (const item of (res.supply_templates ?? []) as Record<string, any>[]) {
    const type = SUPPLY_TYPE_CODES[item.type] ?? "HOME_OPD";

    entries.push(
      entry("supply", item, item.supply, {
        home: type === "HOME_OPD" ? "Y" : "N",
        use: type === "HOME_OPD" ? DEFAULT_FIELDS.supply.use : type,
      }),
    );
  }

  for (const item of (res.miscellaneous_templates ?? []) as Record<string, any>[]) {
    entries.push(entry("misc", item, item.product, { ...DEFAULT_FIELDS.misc }));
  }

  for (const item of (res.drug_templates ?? []) as Record<string, any>[]) {
    const isStat = [6, "6", "STAT"].includes(item.type);
    const isHome = !isStat && ![9, "9", "ONE_DOSE"].includes(item.type);
    const text = (value: unknown) => (value == null ? "" : String(value));

    entries.push(
      entry("drug", item, item.product, {
        ...DEFAULT_FIELDS.drug,
        priority: isStat ? "STAT" : "ROUTINE",
        home: isHome ? "Y" : "N",
        dose: Number(item.dose) ? text(item.dose) : "",
        unit: text(item.unit),
        route: text(item.route),
        site: text(item.site),
        frequency: text(item.frequency),
        prn: text(item.prn),
        duration: Number(item.duration) ? text(item.duration) : "",
        qty: Number(item.duration) && item.frequency ? "" : text(item.quantity),
      }),
    );
  }

  for (const item of (res.doctor_fee_templates ?? []) as Record<string, any>[]) {
    entries.push(entry("df", item, item.doctor_fee_rule, {}));
  }

  for (const item of (res.note_templates ?? []) as Record<string, any>[]) {
    entries.push({
      ...entry("note", item, 0, { detail: item.detail ?? "" }),
      name: NOTE_ITEM_NAME,
      item: { key: "note_0", kind: "note", id: null, name: NOTE_ITEM_NAME, price: 0, raw: {} },
    });
  }

  return entries;
};
